import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeScannerState, Html5QrcodeSupportedFormats } from "html5-qrcode";

/**
 * 安全停止掃描器：html5-qrcode 的 stop() 在「非掃描中（NOT_STARTED）」時是**同步 throw**
 * （"Cannot stop, scanner is not running or paused."），`.catch()` 接不到。React 18 StrictMode
 * 在 dev 會雙跑 effect（mount→unmount→mount），unmount 時 start() 可能還沒完成 → 就會踩到。
 * 這裡先查 getState()、只在 SCANNING/PAUSED 才 stop，並整段包 try/catch 防同步例外。
 */
const stopScannerSafely = (s: Html5Qrcode): void => {
  try {
    const st = s.getState();
    if (st === Html5QrcodeScannerState.SCANNING || st === Html5QrcodeScannerState.PAUSED) {
      s.stop()
        .then(() => s.clear())
        .catch(() => {});
    } else {
      s.clear();
    }
  } catch {
    /* 尚未 start 完成 / 已停止：忽略 */
  }
};

/** 相機取景框 + 掃描線動畫（qrBarcode 用）。提供 onDecode 時開相機即時讀碼。 */
const CORNERS: Array<[v: "top" | "bottom", h: "left" | "right"]> = [
  ["top", "left"],
  ["top", "right"],
  ["bottom", "left"],
  ["bottom", "right"],
];

const cornerStyle = (v: "top" | "bottom", h: "left" | "right"): React.CSSProperties => ({
  position: "absolute",
  [v]: 0,
  [h]: 0,
  width: 32,
  height: 32,
  [`border${v[0].toUpperCase() + v.slice(1)}`]: "3px solid #fff",
  [`border${h[0].toUpperCase() + h.slice(1)}`]: "3px solid #fff",
  borderRadius: v === "top" && h === "left" ? "10px 0 0 0" : v === "top" ? "0 10px 0 0" : h === "left" ? "0 0 0 10px" : "0 0 10px 0",
});

const CAM_ELEM_ID = "onb-camera-scanner";
// 鐵牌 barcode 格式未定 → 支援常見 1D + QR，讀到即回呼。
const SCAN_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.ITF,
];

interface ScanViewportProps {
  height?: number;
  /** 內部取景方框邊長 */
  frame?: number;
  /** 撐滿父容器高度（父需為 flex 容器且本元件為 flex-1）；忽略 height */
  fill?: boolean;
  /** 疊在取景框上的內容（例：示範樣本卡） */
  children?: React.ReactNode;
  /** 提供時 → 開相機即時讀碼；讀到 barcode/QR 內容即呼叫（只呼叫一次）。 */
  onDecode?: (code: string) => void;
}

const ScanViewport = ({ height = 440, frame = 320, fill, children, onDecode }: ScanViewportProps) => {
  const [camError, setCamError] = useState(false);
  const firedRef = useRef(false);
  // 用 ref 存最新的 onDecode：它每次 render 都是新函式（父層 resolveScan 未 memoize），若直接放進
  // 掃描 effect 的依賴，會讓 effect 每次 render 重跑 → 相機不斷停止再啟動 → video.play() 連續被打斷
  // （"play() request was interrupted" 連噴）。改用 ref + 布林 scanEnabled 當依賴，掃描器只啟動一次。
  const onDecodeRef = useRef(onDecode);
  useEffect(() => {
    onDecodeRef.current = onDecode;
  });
  const scanEnabled = !!onDecode;

  // html5-qrcode 在相機啟動中元件被拆卸時，內部 video.play() 的 promise 會 reject
  //（"The play() request was interrupted because the media was removed from the document"）。
  // 那份攔截器原本寫在這裡，但 React 卸載是「先跑 cleanup 移除 listener、再移除 DOM」，
  // reject 發生在那之後的 microtask → 正好沒人接，照樣紅屏。已改為在 app 進入點常駐註冊，
  // 見 utils/func/suppressMediaPlayRejection.ts。

  useEffect(() => {
    if (!scanEnabled) return;
    let scanner: Html5Qrcode | null = null;
    let cancelled = false;
    // 延後啟動：start() 若同步跑，會在 cleanup 設 cancelled 前就開始 play() → 元件被拆時
    // 噴 "play() request was interrupted"。改用 setTimeout 排程，cleanup 直接 clearTimeout 掉
    // 「注定被拆」的那次（React 18 StrictMode dev 的 mount→unmount→remount，或快速重繪），
    // 讓那次相機根本不啟動。
    const startTimer = setTimeout(async () => {
      try {
        scanner = new Html5Qrcode(CAM_ELEM_ID, { formatsToSupport: SCAN_FORMATS, verbose: false });
        if (cancelled) return;
        await scanner.start(
          // 純字串 = 非強制偏好（getUserMedia 語意）：手機用後鏡頭、筆電無後鏡頭自動 fallback 前鏡頭。
          // 注意 html5-qrcode 只接受字串或 { exact }，傳 { ideal } 會被它自己的驗證擋下來。
          { facingMode: "environment" },
          // 不設 qrbox：掃「整個畫面」。原本用 min(vw,vh)*0.7 當掃描框，版面未定/取景框太小時
          // 會算出 < 50px，html5-qrcode 直接丟 "minimum size of config.qrbox is 50px" 而不啟動相機。
          // 視覺上的四角取景框是本元件自己畫的（下方 CORNERS），與 html5-qrcode 的 qrbox 無關。
          { fps: 10 },
          (decodedText: string) => {
            if (firedRef.current) return;
            firedRef.current = true;
            onDecodeRef.current?.(decodedText);
          },
          () => {}, // 每幀讀取失敗（正常，忽略）
        );
        if (cancelled) stopScannerSafely(scanner);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[ScanViewport] camera start failed:", err); // 供排查：權限/裝置/HTTPS 等原因
        if (!cancelled) setCamError(true);
      }
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (scanner) stopScannerSafely(scanner);
    };
  }, [scanEnabled, frame]);

  const showCamera = !!onDecode && !camError;

  return (
    <div
      className={`relative rounded-[18px] overflow-hidden ${fill ? "flex-1 min-h-0" : ""}`}
      style={{ height: fill ? undefined : height, background: "radial-gradient(circle at 50% 45%, #2a3340, #10151c)" }}
    >
      {/* 相機影像（html5-qrcode 會把 <video> 塞進此容器） */}
      {!!onDecode && <div id={CAM_ELEM_ID} className="absolute inset-0" style={{ display: showCamera ? "block" : "none" }} />}
      {/* 取景框 + 掃描線（疊在相機上，不擋點擊） */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ width: frame, height: frame }}>
        {CORNERS.map(([v, h], i) => (
          <div key={i} style={cornerStyle(v, h)} />
        ))}
        <div className="absolute left-1.5 right-1.5 h-0.5 onb-scanline" style={{ background: "#2196f3", boxShadow: "0 0 10px #2196f3" }} />
      </div>
      {camError && (
        <div className="absolute top-3 left-0 right-0 text-center text-[12px] text-white/70 px-4">無法開啟相機，請改用下方示範樣本或手機號碼繼續</div>
      )}
      {children}
    </div>
  );
};

export default ScanViewport;
